import { Color } from '@persona-id/design-tokens';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import useToggle from 'lib/hooks/useToggle';

interface SurroundingBoxAttributes {
  left: number;
  top: number;
  width: number;
  height: number;
}

const DragLayer = styled.div`
  position: fixed;
  pointer-events: none;
  z-index: 2;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;

const SurroundBox = styled.div.attrs<SurroundingBoxAttributes>(({ left, top, width, height }) => ({
  style: {
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
  },
}))<SurroundingBoxAttributes>`
  border: 1px solid ${Color.PrimaryPurple};
  position: absolute;
  background-color: ${Color.CorePurple400}29;
`;

const useDragSurround = ({
  enabled,
  undraggables,
  onSurroundEnd,
}: {
  enabled: boolean;
  undraggables: HTMLDivElement[];
  onSurroundEnd: () => void;
}) => {
  const [startPosition, setStartPosition] = useState<{ x: number | null; y: number | null }>({
    x: null,
    y: null,
  });
  const [endPosition, setEndPosition] = useState<{ x: number | null; y: number | null }>({
    x: null,
    y: null,
  });
  const layerRef = useRef<HTMLDivElement | null>(null);
  // Using ref to store startPosition and endPosition to ensure most updated data
  // even when accessing from event listeners.
  const boxRef = useRef({ startPosition, endPosition });
  const [isSurrounding, { enable: startSurround, disable: endSurround }] = useToggle(false);

  const calculateBox = useCallback((): SurroundingBoxAttributes => {
    const { startPosition, endPosition } = boxRef.current;
    if (!(startPosition.x && startPosition.y && endPosition.x && endPosition.y)) {
      return {
        left: 0,
        top: 0,
        width: 0,
        height: 0,
      };
    }

    let left = Math.min(startPosition.x, endPosition.x);
    let top = Math.min(startPosition.y, endPosition.y);
    let width = Math.abs(startPosition.x - endPosition.x);
    let height = Math.abs(startPosition.y - endPosition.y);

    const boundaryRect = layerRef.current?.parentElement?.getBoundingClientRect();
    if (boundaryRect) {
      const offset = 4;
      const {
        x: boundaryLeft,
        y: boundaryTop,
        width: boundaryDeltaToRight,
        height: boundaryDeltaToBottom,
      } = boundaryRect;
      // horizontal boundaries
      if (boundaryLeft + boundaryDeltaToRight - offset < left + width) {
        width = boundaryLeft + boundaryDeltaToRight - left - offset;
      } else if (boundaryLeft > left - offset) {
        width = width - boundaryLeft + left - offset;
        left = boundaryLeft + offset;
      }

      // vertical boundaries
      if (boundaryTop + boundaryDeltaToBottom - offset < top + height) {
        height = boundaryTop + boundaryDeltaToBottom - top - offset;
      } else if (boundaryTop > top - offset) {
        height = height - boundaryTop + top - offset;
        top = boundaryTop + offset;
      }
    }

    return {
      left,
      top,
      width,
      height,
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const onMouseMove = (e: MouseEvent) => {
      startSurround();
      e.preventDefault();
      e.stopPropagation();

      setEndPosition({
        x: e.clientX,
        y: e.clientY,
      });
      boxRef.current.endPosition = {
        x: e.clientX,
        y: e.clientY,
      };
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.target == null) return;
      if (undraggables.some((undraggable) => undraggable.contains(e.target as Node))) {
        return;
      }
      if (!layerRef.current?.parentElement?.contains(e.target as Node)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();

      setEndPosition({ x: null, y: null });
      setStartPosition({
        x: e.clientX,
        y: e.clientY,
      });
      boxRef.current = {
        endPosition: { x: null, y: null },
        startPosition: {
          x: e.clientX,
          y: e.clientY,
        },
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onMouseUp = (e: MouseEvent) => {
      onSurroundEnd();
      endSurround();
      e.preventDefault();
      e.stopPropagation();

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousedown', onMouseDown);

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', onMouseMove);
    };
  }, [enabled, undraggables, startSurround, endSurround, onSurroundEnd]);

  const isSurrounded = useCallback(
    (node: SurroundingBoxAttributes) => {
      const { height, width, left: x, top: y } = calculateBox();
      return (
        node.left < x + width &&
        node.left + node.width >= x &&
        node.top < y + height &&
        node.top + node.height >= y
      );
    },
    [calculateBox],
  );

  const dragSurroundLayer = (
    <DragLayer ref={layerRef}>
      {isSurrounding && <SurroundBox role='separator' {...calculateBox()} />}
    </DragLayer>
  );

  return {
    dragSurroundLayer,
    surrounding: isSurrounding,
    isSurrounded,
  };
};

export default useDragSurround;
